import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'invoices',
  slug: 'invoices',
  name: 'Invoices',
  description: 'Billing: line-item invoices, payments, and PDF/email delivery.',
  icon: 'RequestQuote',
  endpoints: [
    {
      slug: 'list',
      method: 'GET',
      path: '/api/invoices',
      summary: 'List invoices',
      description: 'Returns every invoice for the tenant with computed totals and payment status.',
      auth: 'session',
      responseExample: `[
{ "id": "inv_44a1", "number": "INV-1044", "billToName": "Example MSP Client", "status": "sent", "total": 1240.00, "dueDate": "2026-08-01" }
]`,
      sourceFile: 'routes/billing.js',
    },
    {
      slug: 'create',
      method: 'POST',
      path: '/api/invoices',
      summary: 'Create an invoice',
      description: 'Creates a draft invoice from line items. Subtotal, tax, and total are computed server-side from taxRate and items.',
      auth: 'session',
      requestBodyExample: `{
"billToName": "Example MSP Client",
"billToEmail": "ap@exampleclient.com",
"dueDate": "2026-08-15",
"taxRate": 0.0725,
"items": [{ "description": "Managed Services - July", "qty": 1, "rate": 1200 }]
}`,
      responseExample: `{ "id": "inv_44a2", "number": "INV-1045", "status": "draft", "subtotal": 1200, "tax": 87, "total": 1287 }`,
      sourceFile: 'routes/billing.js',
    },
    {
      slug: 'record-payment',
      method: 'POST',
      path: '/api/invoices/:id/payments',
      summary: 'Record a payment',
      description: 'Applies a payment to an invoice and updates its paid/outstanding balance and status.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Invoice id.' }],
      requestBodyExample: `{ "amount": 1287, "method": "ach", "date": "2026-08-02" }`,
      responseExample: `{ "ok": true, "status": "paid", "balance": 0 }`,
      sourceFile: 'routes/billing.js',
    },
    {
      slug: 'pdf',
      method: 'GET',
      path: '/api/invoices/:id/pdf',
      summary: 'Download invoice PDF',
      description: 'Returns a generated PDF of the invoice, branded with the tenant\'s configured logo and business details.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Invoice id.' }],
      responseExample: `(binary application/pdf)`,
      sourceFile: 'routes/billing.js',
    },
    {
      slug: 'update',
      method: 'PUT',
      path: '/api/invoices/:id',
      summary: 'Update an invoice',
      description: 'Replaces an invoice\'s bill-to details, line items, tax rate, and notes. Subtotal, tax, and total are recomputed server-side from the submitted items — the same cleaning/rounding path used by create.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Invoice id.' }],
      requestBodyExample: `{
"billToName": "Example MSP Client",
"billToEmail": "ap@exampleclient.com",
"dueDate": "2026-08-20",
"taxRate": 0.0725,
"items": [{ "description": "Managed Services - July", "qty": 1, "rate": 1200 }, { "description": "After-hours support", "qty": 2, "rate": 90 }]
}`,
      responseExample: `{ "id": "inv_44a1", "number": "INV-1044", "status": "sent", "subtotal": 1380, "tax": 100.05, "total": 1480.05, "updatedAt": "2026-07-20T14:02:00.000Z" }`,
      sourceFile: 'routes/billing.js',
    },
    {
      slug: 'delete',
      method: 'DELETE',
      path: '/api/invoices/:id',
      summary: 'Delete an invoice',
      description: 'Permanently removes an invoice, including any recorded payments. There is no soft-delete/undo — for a sent invoice you no longer want to collect on, setting its status to void (see the invoice status update in routes/billing.js) preserves the record instead.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Invoice id.' }],
      responseExample: `{ "ok": true }`,
      sourceFile: 'routes/billing.js',
    },
    {
      slug: 'email',
      method: 'POST',
      path: '/api/invoices/:id/email',
      summary: 'Email an invoice',
      description: 'Sends the invoice to a recipient with a link to the PDF, using the tenant\'s configured mailer. A draft invoice is automatically flipped to sent once the email succeeds.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Invoice id.' }],
      requestBodyExample: `{ "to": "ap@exampleclient.com" }`,
      responseExample: `{ "ok": true }`,
      sourceFile: 'routes/billing.js',
    },
    {
      slug: 'summary',
      method: 'GET',
      path: '/api/invoices/summary',
      summary: 'Invoice summary stats',
      description: 'Returns tenant-wide invoice totals: count, outstanding balance (unpaid/partially-paid, minus credits), paid, overdue, and draft count. Outstanding/overdue reflect the remaining balance, not each invoice\'s full face value.',
      auth: 'session',
      responseExample: `{ "count": 38, "outstanding": 4210.50, "paid": 61840.00, "overdue": 1287.00, "drafts": 3 }`,
      sourceFile: 'routes/billing.js',
    },
  ],
};
